import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { SignUp, useUser } from '@clerk/clerk-react';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { navigateToHome } from '../utils/navigation';
import API_BASE_URL from '../config/api';
import { trackNewUserSignup } from '../utils/metaPixel';

const RegisterPage = () => {
  const { isSignedIn, user, isLoaded } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSyncing, setIsSyncing] = useState(false);

  // FIX 3: After signup, trigger sync FIRST to assign trial, THEN redirect to dashboard
  useEffect(() => {
    if (!isLoaded) return;
    
    // Guard: Only redirect when on the exact base register path AND fully signed in
    const isBaseRegisterPath = location.pathname === '/auth/register';
    
    if (isSignedIn && user && isBaseRegisterPath && !isSyncing) {
      const syncAndRedirect = async () => {
        setIsSyncing(true);
        try {
          console.log('[RegisterPage] Syncing user to assign trial...');
          // Trigger sync to assign trial BEFORE navigating to dashboard
          const response = await fetch(`${API_BASE_URL}/api/clerk/sync-user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              clerk_user_id: user.id,
              email: user.primaryEmailAddress?.emailAddress,
              full_name: user.fullName || '',
              metadata: user.publicMetadata || {}
            })
          });
          
          // Parse response to check if new user (for Meta Pixel tracking)
          try {
            const syncData = await response.json();
            if (syncData && syncData.is_new_user === true) {
              console.log('[RegisterPage] New user detected, firing tracking events');
              trackNewUserSignup();
            }
          } catch (parseErr) {
            // Parse failure must not affect the flow
            console.warn('[RegisterPage] Could not parse sync-user response:', parseErr);
          }
          
          // Reload Clerk user to pick up trial metadata
          await user.reload();
          console.log('[RegisterPage] Sync complete, redirecting to dashboard');
        } catch (err) {
          console.error('[RegisterPage] Sync error:', err);
        }
        
        // Navigate to dashboard (sync is done, trial should be assigned)
        navigate('/dashboard', { replace: true });
      };
      syncAndRedirect();
    }
  }, [isLoaded, isSignedIn, user, location.pathname, navigate, isSyncing]);

  // Show loading while Clerk initializes
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2FA163]"></div>
      </div>
    );
  }

  // NOTE: iOS users CAN now sign up in the app since trial requires no payment
  // Apple Guideline 3.1.1 only restricts purchases, not free account creation

  // Show loading state while syncing and redirecting to dashboard
  if (isSignedIn && user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2FA163] mx-auto mb-4"></div>
          <p className="text-gray-600">{isSyncing ? 'Setting up your free trial...' : 'Redirecting to dashboard...'}</p>
          <p className="text-gray-400 text-sm mt-2">You'll have full PRO access for 7 days!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigateToHome(navigate)}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_f9bdf638-ae2b-47cf-8725-a262363e948c/artifacts/s0f7rwx0_INN%20App%20Icon.png" 
                alt="I Need Numbers" 
                className="h-8 w-8 rounded-lg"
              />
              <span 
                className="text-lg font-semibold text-gray-900"
                style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
              >
                I NEED NUMBERS
              </span>
            </div>
            
            <Link 
              to="/auth/login"
              className="text-[#2FA163] hover:text-[#268a54] font-medium"
            >
              Sign In
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Trial Callout */}
          <div className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl px-6 py-4 text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-2xl">🎉</span>
              <span className="text-lg font-bold text-purple-700">7-Day Free PRO Trial</span>
            </div>
            <p className="text-gray-600 text-sm">
              No credit card required. Full access instantly.
            </p>
          </div>

          <div className="text-center mb-6">
            <h1 
              className="text-2xl font-bold text-gray-900"
              style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            >
              Create Your Account
            </h1>
            <p className="text-gray-600 mt-2">
              Start your free 7-day trial today
            </p>
          </div>
          
          <SignUp 
            appearance={{
              elements: {
                rootBox: "mx-auto",
                card: "shadow-none border border-gray-200 rounded-xl",
                headerTitle: "hidden",
                headerSubtitle: "hidden",
                socialButtonsBlockButton: "border border-gray-200 hover:bg-gray-50",
                formButtonPrimary: "bg-[#2FA163] hover:bg-[#268a54]",
                footerActionLink: "text-[#2FA163] hover:text-[#268a54]"
              }
            }}
            routing="path"
            path="/auth/register"
            signInUrl="/auth/login"
            fallbackRedirectUrl="/dashboard"
          />
          
          <div className="mt-6 text-center text-sm text-gray-500">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-[#2FA163] hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-[#2FA163] hover:underline">Privacy Policy</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
